import React from 'react';
import RoadmapCoursePage from './RoadmapCoursePage';
import { ROADMAP_STUDENT_FAQS } from '../utils/roadmapStudentFaqs';

const JAVA_ROADMAP_CONFIG = {
  path: '/roadmap-to-fullstack-java-course',
  seoTitle: 'Roadmap to Fullstack Java Course | Algodoor',
  seoDescription: 'Visual roadmap for Fullstack Java training with Java, Spring Boot, SQL, projects, interview prep, and placement guidance.',
  badge: 'Java Fullstack Roadmap',
  headingPrefix: 'Roadmap to Become a',
  headingHighlight: 'Fullstack Java Developer',
  heroText: 'This roadmap is designed for students targeting backend-heavy roles. Current hiring demand expects more than core Java: companies usually ask for microservices thinking, cloud deployment (AWS/GCP/Azure), and delivery workflow basics.',
  roadmapNotice: 'This page is for guidance. For immediate admissions and practical placement preparation, we currently offer active batches in Fullstack JavaScript and AWS Cloud with DevOps.',
  enrollPath: '/courses',
  syllabusLink: '/syllabus/java-fullstack-roadmap.txt',
  prerequisites: [
    'Basic understanding of programming logic and variables',
    'Comfort with class 12th level math and English comprehension',
    'Laptop with internet and willingness to practice consistently',
    'No prior Java framework experience required',
  ],
  quickStats: [
    { value: '5 Months', label: 'Structured Plan' },
    { value: '3 Major Apps', label: 'Industry Projects' },
    { value: 'AWS + DevOps', label: 'Deployment Track' },
  ],
  techStack: [
    {
      name: 'Core Java',
      note: 'OOP, collections, exception handling',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg',
    },
    {
      name: 'Spring Boot',
      note: 'REST APIs, security, production setup',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg',
    },
    {
      name: 'MySQL',
      note: 'SQL design, joins, indexing',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
    },
    {
      name: 'React Frontend',
      note: 'Frontend integration and API UI',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
    },
    {
      name: 'AWS',
      note: 'Deployment and cloud essentials',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg',
    },
    {
      name: 'DevOps + GitHub',
      note: 'CI/CD pipelines and team workflow',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg',
    },
  ],
  studentImages: [
    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1544723795-3fb6469f5b39?w=200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=200&auto=format&fit=crop&q=80',
  ],
  overviewCards: [
    {
      title: 'What Makes This Different',
      text: 'This track combines Java backend depth with frontend integration so students can build complete applications, not only isolated APIs.',
    },
    {
      title: 'Syllabus Translation',
      text: 'Core Java, OOP, SQL, Spring Boot, REST, security, microservices foundations, cloud deployment expectations, interview rounds, and placement preparation run in one roadmap.',
    },
    {
      title: 'Hands-On Build Plan',
      text: 'Students build an employee management app, e-commerce backend, and one capstone fullstack product with secure login and role access.',
    },
    {
      title: 'Career Layer',
      text: 'Resume shaping, Java interview drills, system discussion practice, and mock HR rounds are included in the final phase.',
    },
  ],
  roadmapSteps: [
    {
      title: 'Month 1: Java Fundamentals + OOP',
      shortLabel: 'Core Java',
      icon: '☕',
      summary: 'Start with language confidence and coding discipline.',
      bullets: [
        'Data types, control flow, methods, collections',
        'OOP principles with practical coding tasks',
        'Problem solving and debugging habits',
      ],
      tooltip: 'Goal: write clean Java programs independently by the end of month 1.',
    },
    {
      title: 'Month 2: SQL + Backend Foundation',
      shortLabel: 'Database',
      icon: '🗄️',
      summary: 'Build a strong backend base with data modeling.',
      bullets: [
        'SQL queries, joins, schema and normalization',
        'JDBC basics and database interaction',
        'Service layer thinking for real apps',
      ],
      tooltip: 'Goal: design relational tables and connect Java services with database queries.',
    },
    {
      title: 'Month 3: Spring Boot Core + Security',
      shortLabel: 'Spring',
      icon: '🍃',
      summary: 'Design APIs and production-ready backend architecture.',
      bullets: [
        'Spring Boot setup, controllers, services, repositories',
        'Validation, exception handling and JWT security',
        'Build RESTful APIs with role-based access',
      ],
      tooltip: 'Goal: ship secured APIs with reusable architecture and error handling.',
    },
    {
      title: 'Month 4: Frontend Integration + GitHub Workflow',
      shortLabel: 'Integration',
      icon: '🔗',
      summary: 'Connect backend to UI and deploy complete app.',
      bullets: [
        'Integrate React screens with Java APIs',
        'GitHub branching, PR flow and code reviews',
        'End-to-end testing and error handling',
      ],
      tooltip: 'Goal: manage a team-style project workflow using GitHub from feature to merge.',
    },
    {
      title: 'Month 5: AWS Deployment + DevOps + Placement Track',
      shortLabel: 'Career',
      icon: '☁️',
      summary: 'Get interview-ready with structured revision and mocks.',
      bullets: [
        'Deploy Java app on AWS and manage environment configuration',
        'CI/CD basics and deployment monitoring practices',
        'Core Java and Spring interview questions',
        'System design basics for freshers',
        'Mock interviews with detailed feedback',
      ],
      tooltip: 'Goal: run one deployment pipeline and confidently explain your project in interviews.',
    },
  ],
  placementStats: [
    { label: 'Students with complete project portfolio', value: 81 },
    { label: 'Interview conversion potential', value: 74 },
    { label: 'Java hiring stability in service/product firms', value: 86 },
  ],
  interviewFocus: [
    'OOP design reasoning and Java collections usage',
    'Spring Boot annotations, lifecycle and architecture',
    'Microservices basics: service boundaries, API gateway, resilience patterns',
    'SQL optimization, joins and indexing basics',
    'Cloud topics: deployment flow on AWS/GCP/Azure and monitoring basics',
    'Authentication and authorization implementation',
    'Project explanation with clear trade-offs',
  ],
  jobRoles: [
    'Java Backend Developer',
    'Spring Boot Developer',
    'Microservices Developer (Junior)',
    'Cloud-Enabled Java Developer',
    'Fullstack Java Developer',
    'Software Engineer - Java Track',
    'API Developer (Entry Level)',
  ],
  faqs: ROADMAP_STUDENT_FAQS,
};

const RoadmapFullstackJavaPage = () => {
  return <RoadmapCoursePage config={JAVA_ROADMAP_CONFIG} />;
};

export default RoadmapFullstackJavaPage;
